import { useContext, useEffect, useState } from "react";
import VolunteerActivism from "@mui/icons-material/VolunteerActivism";
import { Box, Chip, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import type { InferType } from "yup";

import type {
  FormContextData,
  MentalHealthCareProvider,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import { isGuestUser } from "@thalamos/aspire-common";

import { getTemplate } from "../FormProgressPage/helpers/requestWorkItemActions.js";
import { soadAssessmentLogic } from "../FormProgressPage/soad-assessment/soadAssessmentLogic.js";
import { TrainingDialog } from "./TrainingDialog.js";
import { api, apiHooks } from "~/api.js";
import {
  FormTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import {
  FormFooterSection,
  SignatureDialog,
  TrainingBanner,
} from "~/components/form/index.js";
import type {
  newFormAssigneeData,
  NewResultWithoutAccreditation,
} from "~/components/FormAssignee/NewFormAssigneeDialog.js";
import { NewFormAssigneeDialog } from "~/components/FormAssignee/NewFormAssigneeDialog.js";
import { PatientBanner } from "~/components/layout/index.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { usePatientTimeline } from "~/hooks/apiCalls.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { routeFns } from "~/routePaths.js";
import { logActionToDatadog } from "~/tracing.js";

export type SoadSignAndSendPageProps = Record<string, never>;

function SoadSignAndSendPageInner(props: {
  formContext: FormContextData;
  reloadFormContext: () => Promise<void>;
  patientTimeline: PatientTimelineResponse;
  reloadPatientTimeline: () => Promise<void>;
  mentalHealthCareProviders?: MentalHealthCareProvider[];
}) {
  const {
    formContext,
    reloadFormContext,
    patientTimeline,
    reloadPatientTimeline,
  } = props;

  const userContext = useContext(LoggedInUserContext);
  const isGuest = isGuestUser(userContext?.user);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dialogs = useDialogs();

  const {
    canSave,
    saveAction,
    soadReportForm: { form },
    amendInProgress,
    formNeedsProviderParticipant,
    formNeedsCqcParticipant,
  } = soadAssessmentLogic(
    formContext,
    userContext!.user,
    navigate,
    formContext?.activeTeamworkWorkItem !== undefined,
  );

  const [signing, setSigning] = useState(
    amendInProgress && !formNeedsProviderParticipant,
  );
  const [saveDefaultSignature, setSaveDefaultSignature] = useState(false);
  const cqcSendRequest = userContext?.user.cqcInformation
    ? {
        type: "full-user" as const,
        teamId: userContext.user.cqcInformation.teamId,
      }
    : null;

  const [searchResults, setSearchResults] = useState<
    NewResultWithoutAccreditation[]
  >([]);

  const [recipients, setRecipients] = useState<
    InferType<typeof newFormAssigneeData>[]
  >([]);

  // Redirect if it is not appropriate to be on this page.
  if (!canSave || saveAction !== "review") {
    navigate(routeFns.formContextPage(formContext.id, formContext.patientId));
    return null;
  }

  return (
    <>
      <Stack
        sx={{
          gap: 1,
          mb: 3,
        }}
      >
        <TrainingBanner />
        <PatientBanner
          patient={formContext.patient}
          nhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
          disableSticky
          isHorizontalLineHidden
          patientTimeline={patientTimeline}
          reloadPatientTimeline={() => {
            void reloadPatientTimeline();
          }}
          isHidden={true}
        />
      </Stack>
      {signing && (
        <SignatureDialog
          onClose={() => {
            if (amendInProgress) {
              navigate(
                routeFns.formContextPage(formContext.id, formContext.patientId),
              );
            } else {
              setSigning(false);
            }
          }}
          onHappyWithSignature={async (signature) => {
            if (!signature) {
              return;
            }

            const signResult = await api.soad.signAndShareSoadAssessment(
              formContext.id,
              {
                signatureBase64: signature,
                sendRequests: [
                  ...(cqcSendRequest &&
                  (!amendInProgress || formNeedsCqcParticipant)
                    ? [cqcSendRequest]
                    : []),
                  ...recipients.map((recipient) => {
                    if (recipient.type === "guest") {
                      return {
                        type: "guest" as const,
                        email: recipient.email,
                        name: recipient.name,
                        mentalHealthCareProviderId:
                          recipient.mentalHealthCareProviderId,
                      };
                    }
                    return {
                      type: "full-user" as const,
                      teamId: recipient.teamId!,
                      assignedUserId: recipient.assignedUserId,
                      mentalHealthCareProviderId:
                        recipient.mentalHealthCareProviderId,
                    };
                  }),
                ],
              },
            );

            if (signResult.status !== 204) {
              const errorMessage = amendInProgress
                ? "Failed to amend assessment"
                : String(
                    (signResult.data as any)?.reason ??
                      "Failed to sign and send SOAD assessment",
                  );
              renderErrorToast({ message: errorMessage });
              return;
            } else {
              if (saveDefaultSignature && !userContext?.user.defaultSignature)
                await api.users.update(userContext!.user.id, {
                  defaultSignature: signature,
                });

              if (userContext?.user.sessionContext?.isTrainingOrganisation) {
                await dialogs.open(TrainingDialog);
              }
              renderSuccessToast({
                message: amendInProgress
                  ? "Amendment completed"
                  : "SOAD assessment signed and sent",
              });

              logActionToDatadog("SOAD assessment signed and sent", {
                workItemId: formContext.activeTeamworkWorkItem?.id,
                formContextId: formContext.id,
                recipientDetails: recipients.map((recipient) => ({
                  email: recipient.email,
                  name: recipient.name,
                  teamName: recipient.teamName,
                })),
              });
              await reloadFormContext();
              navigate(
                routeFns.formContextPage(formContext.id, formContext.patientId),
              );
            }
          }}
          userDefaultSignature={userContext?.user.defaultSignature}
          confirmButtonLabel={t("buttonLabels.confirm")}
          clearButtonLabel={t("buttonLabels.clear")}
          saveDefaultSignature={saveDefaultSignature}
          setSaveDefaultSignature={setSaveDefaultSignature}
          allowUseOfDefaultSignature={!isGuest}
        />
      )}
      {(!amendInProgress || formNeedsProviderParticipant) && (
        <Box sx={{ minHeight: "calc(100vh - 200px)" }}>
          <FormTitle subtitleText={"Sign and Send SOAD Assessment"} />
          <Box sx={{ mb: "2rem" }}>
            <Chip
              icon={<VolunteerActivism />}
              color="warning"
              label={` Form will be automatically shared to 
          ${userContext?.user.cqcInformation?.teamName} on 
          ${userContext?.user.cqcInformation?.teamEmail}`}
            ></Chip>
          </Box>

          <NewFormAssigneeDialog
            hideSendButton
            recipients={recipients}
            setRecipients={setRecipients}
            formId={form!.id}
            formContext={formContext}
            reloadFormContext={() => {}}
            availableActions={[]}
            searchAssignmentOptions={async (formDetails, searchValue) => {
              return api.formTemplates.searchAssignmentOptions({
                id: formDetails.template.id,
                version: formDetails.template.version,
                query: searchValue,
                part: formDetails.part,
              });
            }}
            formDetails={getTemplate(formContext)}
            userId={userContext!.user.id}
            userEmail={userContext!.user.email}
            userTeamId={userContext!.user.sessionContext?.teamId}
            userDataControllerId={
              userContext!.user.sessionContext?.dataControllerId
            }
            userDataControllerName={
              userContext!.user.sessionContext?.dataControllerName
            }
            // Data controller warning is never shown for SOAD
            dataControllerWarningEnabled={false}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            onSave={async (
              _formData: InferType<typeof newFormAssigneeData>,
            ) => {}}
            navigate={navigate}
            guestAccreditationOptions={[]}
            signForm={undefined}
            mentalHealthCareProviders={props.mentalHealthCareProviders}
          />
        </Box>
      )}
      <FormFooterSection
        isForcedSticky={true}
        isSticky
        disableSubmit={recipients.length === 0}
        onCancel={() => {
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
          );
        }}
        onSave={() => setSigning(true)}
        discardLabel={t("buttonLabels.goBack")}
        saveLabel={t("buttonLabels.sign")}
      />
    </>
  );
}

export function SoadSignAndSendPage(_props: SoadSignAndSendPageProps) {
  const { formContextId, patientId } = useParams();
  const navigate = useNavigate();

  const [
    { data: form, loading: formLoading, response: formResponse },
    reloadFormContext,
  ] = apiHooks.forms.getFormContext(formContextId!);

  const [
    {
      data: mentalHealthCareProviders,
      loading: mentalHealthCareProvidersLoading,
    },
  ] = apiHooks.mentalHealthCareProvider.getAll();

  useEffect(() => {
    if (!formLoading) {
      if (formResponse?.status === 404) {
        navigate(routeFns.notFound(), { replace: true });
      }
    }
  }, [form, formLoading, formResponse?.status, navigate]);

  const {
    patientTimeline,
    reloadPatientTimeline,
    patientTimelineCompleteLoading,
  } = usePatientTimeline({
    patientId,
  });

  return formLoading ||
    !form ||
    mentalHealthCareProvidersLoading ||
    patientTimelineCompleteLoading ||
    !patientTimeline ? (
    <CircularProgress />
  ) : (
    <SoadSignAndSendPageInner
      formContext={form}
      reloadFormContext={async () => {
        reloadFormContext();
      }}
      patientTimeline={patientTimeline}
      reloadPatientTimeline={async () => {
        reloadPatientTimeline();
      }}
      mentalHealthCareProviders={mentalHealthCareProviders}
    />
  );
}
